import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { horsesAPI, referenceAPI, horseRegistrationsAPI, getMediaUrl } from '../services/api';
import AddReferenceModal from './AddReferenceModal';
import AddLocationModal from './AddLocationModal';
import PhotoCropDialog from './PhotoCropDialog';
import ConfirmDialog from './ConfirmDialog';
import { useHorseModal } from '../context/HorseModalContext';
import { deriveCurrentStatusAfterSaleDateChange, ALL_HORSE_STATUSES } from '../utils/horseStatus';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';

const OWNER_TYPE_OPTIONS = [
  { value: 'owned', label: 'Owned' },
  { value: 'client', label: 'Client' },
  { value: 'leased', label: 'Leased' },
  { value: 'reference', label: 'Reference' },
];

const emptyForm = {
  name: '',
  registered_name: '',
  type_id: '',
  breed_id: '',
  color_id: '',
  date_of_birth: '',
  location_id: '',
  owner_type: 'owned',
  current_status: 'active',
  sire_id: '',
  dam_id: '',
  sire_sire_id: '',
  dam_sire_id: '',
  microchip: '',
  height: '',
  weight: '',
  notes: '',
  description: '',
  color_panel: '',
  dna_test: '',
  disease_panel: '',
  registration_targets: [],
  registration_submitted_dates: {},
  blanket_size: '',
  gestation_days_override: '',
  purchase_amount: '',
  sale_amount: '',
  date_purchased: '',
  date_purchase_paid: '',
  date_sold: '',
  date_sale_paid: '',
};

const formatDateForInput = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const str = String(value);
  const datePart = str.trim().slice(0, 10);
  if (datePart.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return datePart;
  }
  return '';
};

// { orgName: date } map from the API, with every value coerced to what a
// type="date" input accepts. Blank/unparseable entries are dropped so an org
// never reads as "submitted" on the strength of a value we can't display.
const normalizeSubmittedDates = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return Object.entries(value).reduce((acc, [orgName, rawDate]) => {
    const formatted = formatDateForInput(rawDate);
    if (formatted) {
      acc[orgName] = formatted;
    }
    return acc;
  }, {});
};

const normalizeHorseForForm = (horse) => ({
  ...emptyForm,
  ...horse,
  name: horse.name || '',
  registered_name: horse.registered_name || '',
  type_id: horse.type_id ? String(horse.type_id) : '',
  breed_id: horse.breed_id ? String(horse.breed_id) : '',
  color_id: horse.color_id ? String(horse.color_id) : '',
  date_of_birth: formatDateForInput(horse.date_of_birth),
  location_id: horse.location_id ? String(horse.location_id) : '',
  owner_type: horse.owner_type || 'owned',
  current_status: horse.current_status || 'active',
  sire_id: horse.sire_id ? String(horse.sire_id) : '',
  dam_id: horse.dam_id ? String(horse.dam_id) : '',
  sire_sire_id: horse.sire_sire_id ? String(horse.sire_sire_id) : '',
  dam_sire_id: horse.dam_sire_id ? String(horse.dam_sire_id) : '',
  microchip: horse.microchip || '',
  height: horse.height ?? '',
  weight: horse.weight ?? '',
  notes: horse.notes || '',
  description: horse.description || '',
  color_panel: horse.color_panel || '',
  dna_test: horse.dna_test || '',
  disease_panel: horse.disease_panel || '',
  registration_targets: Array.isArray(horse.registration_targets) ? horse.registration_targets : [],
  registration_submitted_dates: normalizeSubmittedDates(horse.registration_submitted_dates),
  blanket_size: horse.blanket_size || '',
  gestation_days_override: horse.gestation_days_override != null ? horse.gestation_days_override : '',
  purchase_amount: horse.purchase_amount != null ? horse.purchase_amount : '',
  sale_amount: horse.sale_amount != null ? horse.sale_amount : '',
  date_purchased: formatDateForInput(horse.date_purchased),
  date_purchase_paid: formatDateForInput(horse.date_purchase_paid),
  date_sold: formatDateForInput(horse.date_sold),
  date_sale_paid: formatDateForInput(horse.date_sale_paid),
});

const HorseModal = ({ open, onClose, horse, onSuccess, zIndex = 1300 }) => {
  const notify = useNotify();
  const { openHorseModal } = useHorseModal();
  
  // Calculate menu z-index to be well above the modal
  // Use a larger increment to ensure menus appear above nested modals
  const menuZIndex = zIndex + 50;
  const [horseTypes, setHorseTypes] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [colors, setColors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allHorses, setAllHorses] = useState([]);
  const [horseRegistrations, setHorseRegistrations] = useState([]);

  const TRACKED_ORGS = ['AQHA', 'APHA', 'NRHA', 'NRBC'];

  // Modal states for adding new reference values
  const [openTypeModal, setOpenTypeModal] = useState(false);
  const [openBreedModal, setOpenBreedModal] = useState(false);
  const [openColorModal, setOpenColorModal] = useState(false);
  const [openLocationModal, setOpenLocationModal] = useState(false);

  const [formData, setFormData] = useState(emptyForm);
  const [photos, setPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [pendingImageSrc, setPendingImageSrc] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      loadReferenceData();
      loadAllHorses();
      if (horse && horse.id) {
        // Editing existing horse
        setFormData(normalizeHorseForForm(horse));
        setPhotos(Array.isArray(horse.photos) ? horse.photos : []);
        horseRegistrationsAPI.getAll(horse.id)
          .then(res => setHorseRegistrations(Array.isArray(res.data) ? res.data : []))
          .catch(() => setHorseRegistrations([]));
      } else if (horse) {
        // Adding new horse with default values (e.g., defaultType)
        setFormData({
          ...emptyForm,
          type_id: horse.type_id ? String(horse.type_id) : '',
        });
        setPhotos([]);
        setHorseRegistrations([]);
      } else {
        setFormData(emptyForm);
        setPhotos([]);
        setHorseRegistrations([]);
      }
    }
  }, [open, horse]);

  const loadReferenceData = async () => {
    try {
      const [typesRes, breedsRes, colorsRes, locationsRes] = await Promise.all([
        referenceAPI.getHorseTypes(),
        referenceAPI.getBreeds(),
        referenceAPI.getColors(),
        referenceAPI.getLocations(),
      ]);
      setHorseTypes(toArray(typesRes.data, 'horse types'));
      setBreeds(toArray(breedsRes.data, 'breeds'));
      setColors(toArray(colorsRes.data, 'colors'));
      setLocations(toArray(locationsRes.data, 'locations'));
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  };

  const loadAllHorses = async () => {
    try {
      const response = await horsesAPI.getAll({});
      setAllHorses(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading all horses:', error);
      setAllHorses([]);
    }
  };

  const getHorseSortKey = (horse) => {
    const r = (horse?.registered_name ?? '').toString().trim();
    const n = (horse?.name ?? '').toString().trim();
    return r || n || `Horse #${horse?.id}` || '';
  };

  const getLocationOptionLabel = (location) => {
    if (!location) return '';
    const extractBusiness = (value) => {
      if (!value) return null;
      const parts = value.split(' — ');
      return parts[parts.length - 1] || null;
    };

    const referenceParts = [
      extractBusiness(location.trainer_display),
      extractBusiness(location.vet_display),
    ].filter(Boolean);

    if (referenceParts.length === 0) {
      return location.name;
    }

    const uniqueParts = Array.from(new Set(referenceParts));
    return `${location.name} — ${uniqueParts.join(' • ')}`;
  };

  const getPedigreeHorseOptionLabel = (horse) => {
    const registeredName = horse.registered_name?.trim();
    const barnName = horse.name?.trim();
    const hasBothNames = barnName && registeredName && registeredName !== barnName;
    if (registeredName && barnName && hasBothNames) {
      return `${registeredName} (${barnName})`;
    }
    if (registeredName) return registeredName;
    if (barnName) return barnName;
    return `Horse #${horse.id}`;
  };

  const breedOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...[...breeds].sort((a, b) => a.name.localeCompare(b.name)).map((breed) => ({ id: String(breed.id), label: breed.name })),
  ], [breeds]);

  const colorOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...[...colors].sort((a, b) => a.name.localeCompare(b.name)).map((color) => ({ id: String(color.id), label: color.name })),
  ], [colors]);

  const locationOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...[...locations]
      .sort((a, b) => getLocationOptionLabel(a).localeCompare(getLocationOptionLabel(b)))
      .map((location) => ({ id: String(location.id), label: getLocationOptionLabel(location) })),
  ], [locations]);

  const currentStatusOptions = ALL_HORSE_STATUSES.filter((o) => o.value !== 'current');

  const handleClose = () => {
    setFormData(emptyForm);
    setPhotos([]);
    onClose();
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file || !horse?.id) return;
    setPendingImageSrc(URL.createObjectURL(file));
    setCropDialogOpen(true);
  };

  const closeCropDialog = () => {
    if (pendingImageSrc) URL.revokeObjectURL(pendingImageSrc);
    setPendingImageSrc(null);
    setCropDialogOpen(false);
  };

  const handleCropConfirm = async (blob, setAsHero) => {
    if (!horse?.id) return;
    setPhotoUploading(true);
    try {
      const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
      const res = await horsesAPI.uploadPhoto(horse.id, file, '', setAsHero);
      setPhotos((prev) => {
        const updated = setAsHero ? prev.map((p) => ({ ...p, is_hero: false })) : prev;
        return [...updated, res.data];
      });
      closeCropDialog();
    } catch (err) {
      console.error('Photo upload failed', err);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSetPrimary = async (photoId) => {
    if (!horse?.id) return;
    try {
      await horsesAPI.updatePhoto(horse.id, photoId, { is_primary: true });
      setPhotos((prev) => prev.map((p) => ({ ...p, is_primary: p.id === photoId })));
    } catch (err) {
      console.error('Set primary failed', err);
    }
  };

  const [deletePhotoTarget, setDeletePhotoTarget] = useState(null);

  const handleDeletePhotoClick = (photoId) => setDeletePhotoTarget(photoId);
  const handleDeletePhotoCancel = () => setDeletePhotoTarget(null);

  const handleDeletePhotoConfirm = async () => {
    if (!horse?.id) return;
    const photoId = deletePhotoTarget;
    try {
      await horsesAPI.deletePhoto(horse.id, photoId);
      setPhotos((prev) => {
        const remaining = prev.filter((p) => p.id !== photoId);
        if (remaining.length > 0 && !remaining.some((p) => p.is_primary)) {
          remaining[0] = { ...remaining[0], is_primary: true };
        }
        return remaining;
      });
    } catch (err) {
      console.error('Delete photo failed', err);
    } finally {
      setDeletePhotoTarget(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'date_sale_paid' || name === 'date_sold') {
        const status = deriveCurrentStatusAfterSaleDateChange(prev, name, value);
        if (status !== undefined) next.current_status = status;
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    try {
      // Prepare data for submission - convert empty strings to null for ID fields
      const submitData = {
        ...formData,
        type_id: formData.type_id === '' ? null : (formData.type_id ? parseInt(formData.type_id) : null),
        breed_id: formData.breed_id === '' ? null : (formData.breed_id ? parseInt(formData.breed_id) : null),
        color_id: formData.color_id === '' ? null : (formData.color_id ? parseInt(formData.color_id) : null),
        location_id: formData.location_id === '' ? null : (formData.location_id ? parseInt(formData.location_id) : null),
        sire_id: formData.sire_id === '' ? null : (formData.sire_id ? parseInt(formData.sire_id) : null),
        dam_id: formData.dam_id === '' ? null : (formData.dam_id ? parseInt(formData.dam_id) : null),
        sire_sire_id: formData.sire_sire_id === '' ? null : (formData.sire_sire_id ? parseInt(formData.sire_sire_id) : null),
        dam_sire_id: formData.dam_sire_id === '' ? null : (formData.dam_sire_id ? parseInt(formData.dam_sire_id) : null),
        gestation_days_override: formData.gestation_days_override === '' ? null : (formData.gestation_days_override ? parseInt(formData.gestation_days_override, 10) : null),
      };
      
      let response;
      if (horse && horse.id) {
        // Editing existing horse
        response = await horsesAPI.update(horse.id, submitData);
      } else {
        // Adding new horse
        response = await horsesAPI.create(submitData);
      }
      
      // Reset form data before closing
      setFormData(emptyForm);
      
      // Call onSuccess (which will close the modal via the context)
      if (onSuccess) {
        onSuccess(response.data);
      } else {
        // If no onSuccess callback, just close the modal
        onClose();
      }
    } catch (error) {
      console.error('Error saving horse:', error);
      const message = error.response?.data?.error || error.message || 'Error saving horse';
      notify.error(message);
    }
  };

  // Handler for adding new horse type
  const handleAddHorseType = async (data) => {
    const response = await referenceAPI.createHorseType(data);
    const typesResponse = await referenceAPI.getHorseTypes();
    setHorseTypes(toArray(typesResponse.data, 'horse types'));
    setFormData((prev) => ({ ...prev, type_id: String(response.data.id) }));
  };

  // Handler for adding new breed
  const handleAddBreed = async (data) => {
    const response = await referenceAPI.createBreed(data);
    const breedsResponse = await referenceAPI.getBreeds();
    setBreeds(toArray(breedsResponse.data, 'breeds'));
    setFormData((prev) => ({ ...prev, breed_id: String(response.data.id) }));
  };

  // Handler for adding new color
  const handleAddColor = async (data) => {
    const response = await referenceAPI.createColor(data);
    const colorsResponse = await referenceAPI.getColors();
    setColors(toArray(colorsResponse.data, 'colors'));
    setFormData((prev) => ({ ...prev, color_id: String(response.data.id) }));
  };

  // Handler for adding new location
  const handleAddLocation = async (data) => {
    const response = await referenceAPI.createLocation(data);
    const locationsResponse = await referenceAPI.getLocations();
    setLocations(toArray(locationsResponse.data, 'locations'));
    setFormData((prev) => ({ ...prev, location_id: String(response.data.id) }));
  };

  // Handler for adding new sire
  const handleAddSire = async (newHorse) => {
    await loadAllHorses();
    setFormData((prev) => ({ ...prev, sire_id: newHorse.id }));
  };

  // Handler for adding new dam
  const handleAddDam = async (newHorse) => {
    await loadAllHorses();
    setFormData((prev) => ({ ...prev, dam_id: newHorse.id }));
  };

  // Handler for adding new sire's sire
  const handleAddSireSire = async (newHorse) => {
    await loadAllHorses();
    setFormData((prev) => ({ ...prev, sire_sire_id: newHorse.id }));
  };

  // Handler for adding new dam's sire
  const handleAddDamSire = async (newHorse) => {
    await loadAllHorses();
    setFormData((prev) => ({ ...prev, dam_sire_id: newHorse.id }));
  };

  // Open sire modal via context
  const handleOpenSireModal = () => {
    // Find Stallion type_id for default
    const stallionType = horseTypes.find(t => t.name.toLowerCase() === 'stallion');
    openHorseModal(
      { type_id: stallionType?.id }, // Create a temporary horse object with default type_id
      handleAddSire
    );
  };

  // Open dam modal via context
  const handleOpenDamModal = () => {
    // Find Mare type_id for default
    const mareType = horseTypes.find(t => t.name.toLowerCase() === 'mare');
    openHorseModal(
      { type_id: mareType?.id }, // Create a temporary horse object with default type_id
      handleAddDam
    );
  };

  // Open sire's sire modal via context
  const handleOpenSireSireModal = () => {
    const stallionType = horseTypes.find(t => t.name.toLowerCase() === 'stallion');
    openHorseModal(
      { type_id: stallionType?.id },
      handleAddSireSire
    );
  };

  // Open dam's sire modal via context
  const handleOpenDamSireModal = () => {
    const stallionType = horseTypes.find(t => t.name.toLowerCase() === 'stallion');
    openHorseModal(
      { type_id: stallionType?.id },
      handleAddDamSire
    );
  };

  // Get filtered horse lists for sire/dam dropdowns
  const getSireOptions = () => {
    return (allHorses || []).filter(h => {
      const typeName = h.type?.toLowerCase();
      return typeName === 'stallion' || typeName === 'gelding';
    });
  };

  const getDamOptions = () => {
    return (allHorses || []).filter(h => h.type?.toLowerCase() === 'mare');
  };

  const sireOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...getSireOptions()
      .sort((a, b) => getHorseSortKey(a).localeCompare(getHorseSortKey(b)))
      .map((horse) => ({ id: horse.id, label: getPedigreeHorseOptionLabel(horse) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [allHorses]);

  const damOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...getDamOptions()
      .sort((a, b) => getHorseSortKey(a).localeCompare(getHorseSortKey(b)))
      .map((horse) => ({ id: horse.id, label: getPedigreeHorseOptionLabel(horse) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [allHorses]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              zIndex: zIndex - 1,
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }}
        PaperProps={{
          sx: {
            zIndex: zIndex,
            position: 'relative'
          }
        }}
        sx={{ 
          zIndex: zIndex,
          '& .MuiDialog-container': {
            zIndex: zIndex
          },
          '& .MuiDialog-paper': {
            zIndex: zIndex,
            position: 'relative'
          },
          '& .MuiBackdrop-root': {
            zIndex: zIndex - 1
          }
        }}
      >
        <DialogTitle>{horse && horse.id ? 'Edit Horse' : 'Add New Horse'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Basic Information */}
              <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                <Typography variant="subtitle1" color="primary">
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="name"
                  label="Name *"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="registered_name"
                  label="Registered Name"
                  value={formData.registered_name}
                  onChange={handleInputChange}
                  fullWidth
                  inputProps={{ maxLength: 20 }}
                  helperText="Max 20 characters (AQHA limit)"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="microchip"
                  label="Microchip Number"
                  value={formData.microchip}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              {/* Type with Add Button */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={[...horseTypes].sort((a, b) => a.name.localeCompare(b.name))}
                    getOptionLabel={(type) => type.name}
                    isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                    filterOptions={createPrefixFilter((type) => type.name)}
                    componentsProps={{
                      popper: { disablePortal: true },
                      paper: { sx: { zIndex: menuZIndex } },
                    }}
                    value={horseTypes.find((type) => String(type.id) === formData.type_id) ?? null}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, type_id: newValue ? String(newValue.id) : '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Type" required />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenTypeModal(true)}
                    sx={{ minWidth: '40px', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Breed with Add Button */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={breedOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    componentsProps={{
                      popper: { disablePortal: true },
                      paper: { sx: { zIndex: menuZIndex } },
                    }}
                    value={breedOptions.find((o) => o.id === formData.breed_id) ?? breedOptions[0]}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, breed_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Breed" />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenBreedModal(true)}
                    sx={{ minWidth: '40px', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Color with Add Button */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={colorOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    componentsProps={{
                      popper: { disablePortal: true },
                      paper: { sx: { zIndex: menuZIndex } },
                    }}
                    value={colorOptions.find((o) => o.id === formData.color_id) ?? colorOptions[0]}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, color_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Color" />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenColorModal(true)}
                    sx={{ minWidth: '40px', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="date_of_birth"
                  label="Date of Birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              {/* Registrations */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" sx={{ mt: 2, mb: 0.5 }}>
                  Registrations
                </Typography>
                <Grid container spacing={1} columnSpacing={2}>
                  {TRACKED_ORGS.map((orgName) => {
                    const isChecked = (formData.registration_targets || []).includes(orgName);
                    const hasRecord = horseRegistrations.some(r => r.registration_org_name === orgName);
                    const reg = horseRegistrations.find(r => r.registration_org_name === orgName);
                    const submittedDate = (formData.registration_submitted_dates || {})[orgName] || '';

                    // A registration on file wins over a submitted date, so a date left
                    // behind once the papers arrive is harmless.
                    let dotColor = 'text.disabled';
                    if (isChecked) {
                      if (hasRecord) dotColor = 'success.main';
                      else if (submittedDate) dotColor = 'warning.main';
                      else dotColor = 'error.main';
                    }

                    return (
                      <Grid item xs={12} sm={6} key={orgName} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFormData(prev => {
                                  const targets = prev.registration_targets || [];
                                  const nextDates = { ...(prev.registration_submitted_dates || {}) };
                                  // Untargeting an org drops its date too, so it can't
                                  // resurface if the org is re-checked later.
                                  if (!checked) delete nextDates[orgName];
                                  return {
                                    ...prev,
                                    registration_targets: checked
                                      ? [...targets, orgName]
                                      : targets.filter(t => t !== orgName),
                                    registration_submitted_dates: nextDates,
                                  };
                                });
                              }}
                              size="small"
                              sx={{
                                color: dotColor,
                                '&.Mui-checked': { color: dotColor },
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FiberManualRecordIcon sx={{ fontSize: 10, color: dotColor }} />
                              <Typography variant="body2" fontWeight={600} sx={{ color: isChecked ? dotColor : 'text.secondary' }}>
                                {orgName}
                              </Typography>
                              {reg && (
                                <Typography variant="caption" color="text.secondary">
                                  {reg.registration_number}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{ mr: 0, minWidth: 130 }}
                        />
                        {/* Only while the registration is outstanding — once a number is
                            on file it replaces the date as the meaningful value. */}
                        {isChecked && !hasRecord && (
                          <TextField
                            size="small"
                            variant="standard"
                            label="Submitted"
                            type="date"
                            value={submittedDate}
                            onChange={(e) => {
                              const { value } = e.target;
                              setFormData(prev => {
                                const nextDates = { ...(prev.registration_submitted_dates || {}) };
                                if (value) nextDates[orgName] = value;
                                else delete nextDates[orgName];
                                return { ...prev, registration_submitted_dates: nextDates };
                              });
                            }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 140 }}
                          />
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Green = registration on file · Amber = submitted, awaiting papers · Red = registration needed
                </Typography>
              </Grid>

              {/* Pedigree Information */}
              <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                  Pedigree
                </Typography>
              </Grid>

              {/* Sire with Add Button */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={sireOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    componentsProps={{
                      popper: { disablePortal: true },
                      paper: { sx: { zIndex: menuZIndex } },
                    }}
                    value={sireOptions.find((o) => o.id === (formData.sire_id || '')) ?? sireOptions[0]}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, sire_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Sire (Stallion/Gelding)" />}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleOpenSireModal}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Dam with Add Button */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={damOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    componentsProps={{
                      popper: { disablePortal: true },
                      paper: { sx: { zIndex: menuZIndex } },
                    }}
                    value={damOptions.find((o) => o.id === (formData.dam_id || '')) ?? damOptions[0]}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, dam_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Dam (Mare)" />}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleOpenDamModal}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Sire's Sire (Paternal Grandsire) with Add Button */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={sireOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    componentsProps={{
                      popper: { disablePortal: true },
                      paper: { sx: { zIndex: menuZIndex } },
                    }}
                    value={sireOptions.find((o) => o.id === (formData.sire_sire_id || '')) ?? sireOptions[0]}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, sire_sire_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Sire's Sire (Paternal Grandsire)" />}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleOpenSireSireModal}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Dam's Sire (Maternal Grandsire) with Add Button */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={sireOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    componentsProps={{
                      popper: { disablePortal: true },
                      paper: { sx: { zIndex: menuZIndex } },
                    }}
                    value={sireOptions.find((o) => o.id === (formData.dam_sire_id || '')) ?? sireOptions[0]}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, dam_sire_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Dam's Sire (Maternal Grandsire)" />}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleOpenDamSireModal}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Physical Details */}
              <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                  Physical Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="height"
                  label="Height (hands)"
                  type="number"
                  value={formData.height}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="weight"
                  label="Weight (lbs)"
                  type="number"
                  value={formData.weight}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="blanket_size"
                  label="Blanket Size"
                  value={formData.blanket_size}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="gestation_days_override"
                  label="Gestation Days Override"
                  type="number"
                  value={formData.gestation_days_override}
                  onChange={handleInputChange}
                  helperText="Leave blank for default 340 days. Used when this mare is bred."
                  fullWidth
                  inputProps={{ min: 250, max: 400 }}
                />
              </Grid>

              {/* Location with Add Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={locationOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    componentsProps={{
                      popper: { disablePortal: true },
                      paper: { sx: { zIndex: menuZIndex } },
                    }}
                    value={locationOptions.find((o) => o.id === formData.location_id) ?? locationOptions[0]}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, location_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Location" />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenLocationModal(true)}
                    sx={{ minWidth: '40px', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Status Information */}
              <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                  Status
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  fullWidth
                  disableClearable
                  options={OWNER_TYPE_OPTIONS}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v?.value}
                  filterOptions={createPrefixFilter((o) => o.label)}
                  componentsProps={{
                    popper: { disablePortal: true },
                    paper: { sx: { zIndex: menuZIndex } },
                  }}
                  value={OWNER_TYPE_OPTIONS.find((o) => o.value === formData.owner_type) ?? OWNER_TYPE_OPTIONS[0]}
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({ ...prev, owner_type: newValue?.value ?? '' }))
                  }
                  renderInput={(params) => <TextField {...params} label="Owner Type" />}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  fullWidth
                  disableClearable
                  options={currentStatusOptions}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(o, v) => o.value === v?.value}
                  filterOptions={createPrefixFilter((o) => o.label)}
                  componentsProps={{
                    popper: { disablePortal: true },
                    paper: { sx: { zIndex: menuZIndex } },
                  }}
                  value={currentStatusOptions.find((o) => o.value === formData.current_status) ?? currentStatusOptions[0]}
                  onChange={(e, newValue) =>
                    setFormData((prev) => ({ ...prev, current_status: newValue?.value ?? '' }))
                  }
                  renderInput={(params) => <TextField {...params} label="Status" />}
                />
              </Grid>

              {/* Genetic Tests */}
              <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                  Genetic & Health Testing
                </Typography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="color_panel"
                  label="Color Panel"
                  value={formData.color_panel}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="dna_test"
                  label="DNA Test"
                  value={formData.dna_test}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="disease_panel"
                  label="Disease Panel"
                  value={formData.disease_panel}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              {/* Financial */}
              <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                  Financial History
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="purchase_amount"
                  label="Purchase Amount"
                  type="number"
                  value={formData.purchase_amount}
                  onChange={handleInputChange}
                  fullWidth
                  inputProps={{ step: '0.01' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="sale_amount"
                  label="Sale Amount"
                  type="number"
                  value={formData.sale_amount}
                  onChange={handleInputChange}
                  fullWidth
                  inputProps={{ step: '0.01' }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="date_purchased"
                  label="Date Purchased"
                  type="date"
                  value={formData.date_purchased}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="date_purchase_paid"
                  label="Date Purchase Paid"
                  type="date"
                  value={formData.date_purchase_paid}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="date_sold"
                  label="Date Sold"
                  type="date"
                  value={formData.date_sold}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="date_sale_paid"
                  label="Date Sale Paid"
                  type="date"
                  value={formData.date_sale_paid}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              {/* AllBreed */}
              {horse && horse.id && (
                <>
                  <Grid item xs={12} sx={{ pb: 0, mb: -0.5 }}>
                    <Typography variant="subtitle1" color="primary" sx={{ mt: 2 }}>
                      AllBreed
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                  size="small"
                      name="allbreed_horse_id"
                      label="AllBreed Horse ID"
                      value={formData.allbreed_horse_id || ''}
                      onChange={handleInputChange}
                      fullWidth
                      placeholder="e.g. my-horse-name-aBcD1234"
                    />
                  </Grid>
                </>
              )}

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="notes"
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>

              {/* Public Description */}
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="description"
                  label="Public Description (shown on ranch website)"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>

              {/* Photos — only for existing horses */}
              {horse && horse.id && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ pb: 0, mb: -0.5 }}>
                    Photos
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {photos.map((photo) => (
                      <Box
                        key={photo.id}
                        sx={{
                          position: 'relative',
                          width: 90,
                          height: 90,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: photo.is_primary ? '2px solid' : '2px solid transparent',
                          borderColor: photo.is_primary ? 'primary.main' : 'transparent',
                        }}
                      >
                        <img
                          src={getMediaUrl(photo.url)}
                          alt={photo.caption || 'Horse photo'}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Box sx={{ position: 'absolute', top: 0, right: 0, display: 'flex', flexDirection: 'column' }}>
                          <Tooltip title={photo.is_primary ? 'Primary photo' : 'Set as primary'}>
                            <IconButton
                              size="small"
                              sx={{ p: 0.25, bgcolor: 'rgba(0,0,0,0.5)', color: photo.is_primary ? 'warning.main' : 'white' }}
                              onClick={() => !photo.is_primary && handleSetPrimary(photo.id)}
                            >
                              {photo.is_primary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete photo">
                            <IconButton
                              size="small"
                              sx={{ p: 0.25, bgcolor: 'rgba(0,0,0,0.5)', color: 'error.light' }}
                              onClick={() => handleDeletePhotoClick(photo.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    style={{ display: 'none' }}
                    onChange={handlePhotoUpload}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={photoUploading}
                  >
                    {photoUploading ? 'Uploading…' : 'Upload Photo'}
                  </Button>
                  <PhotoCropDialog
                    open={cropDialogOpen}
                    imageSrc={pendingImageSrc}
                    onConfirm={handleCropConfirm}
                    onCancel={closeCropDialog}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {horse && horse.id ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nested Add Reference Modals */}
      <AddReferenceModal
        open={openTypeModal}
        onClose={() => setOpenTypeModal(false)}
        onSave={handleAddHorseType}
        title="Horse Type"
        label="Type Name"
      />

      <AddReferenceModal
        open={openBreedModal}
        onClose={() => setOpenBreedModal(false)}
        onSave={handleAddBreed}
        title="Breed"
        label="Breed Name"
      />

      <AddReferenceModal
        open={openColorModal}
        onClose={() => setOpenColorModal(false)}
        onSave={handleAddColor}
        title="Color"
        label="Color Name"
      />

      <AddLocationModal
        open={openLocationModal}
        onClose={() => setOpenLocationModal(false)}
        onSave={handleAddLocation}
      />

      <ConfirmDialog
        open={!!deletePhotoTarget}
        title="Delete Photo"
        message="Permanently delete this photo? This cannot be undone."
        onConfirm={handleDeletePhotoConfirm}
        onCancel={handleDeletePhotoCancel}
      />
    </>
  );
};

export default HorseModal;

